import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Field, Fields, FieldArray } from 'redux-form';

import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import Button from 'components/common/Button';
import IconButton from 'components/common/IconButton';
import Link from 'components/common/Link';
import Tooltip from 'components/common/Tooltip';
import AutosuggestField from 'components/fields/AutosuggestField';
import TextField from 'components/fields/TextField';
import CheckboxField from 'components/fields/CheckboxField';
import DatepickerField from 'components/fields/DatepickerField';
import TimepickerField from 'components/fields/TimepickerField';
import IconCross from 'components/icons/IconCross';
import EditTextField from 'components/fields/EditTextField';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import hexToRgb from 'styles/hexToRgb';

import dateFormat from 'config/dateFormat';

import ProductClaimCMNAMBField from './ProductClaimCMNAMBField';
import ProductClaimNarrativeField from './ProductClaimNarrativeField';
import ProductClaimDeliveryFields from './ProductClaimDeliveryFields';
import ProductClaimModifiersFieldArray from './ProductClaimModifiersFieldArray';
import ProductClaimTypeFields from './ProductClaimTypeFields';
import ProductClaimRefillFields from './ProductClaimRefillFields';
import ProductClaimRefillDatesField from './ProductClaimRefillDatesField';
import ProductClaimTotalChargeFields from './ProductClaimTotalChargeFields';
import ProductClaimUnitsSplitForClaimFields from './ProductClaimUnitsSplitForClaimFields';
import ProductClaimDiagnosisFields from './ProductClaimDiagnosisFields';
import ProductClaimAssignedUserFields from './ProductClaimAssignedUserFields';
import ProductClaimStatusFields from './ProductClaimStatusFields';
import ProductClaimIsRejectedField from './ProductClaimIsRejectedField';

const styles = theme => ({
  root: {
    position: 'relative',
    marginTop: 16,
    [theme.breakpoints.down('sm')]: {
      marginTop: 12,
    },
  },
  rootFirst: {
    marginTop: 15,
  },
  grid: {
    marginBottom: 8,
  },
  gridShrink: {
    flexShrink: 1,
    minWidth: 0,
  },
  gridType: {
    maxWidth: '14%',
    flexBasis: '14%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '32%',
      flexBasis: '32%',
    },
  },
  gridItemDateOfService: {
    maxWidth: '27%',
    flexBasis: '27%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '60%',
      flexBasis: '60%',
    },
  },
  gridItemModifiers: {
    maxWidth: '18%',
    flexBasis: '18%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '40%',
      flexBasis: '40%',
    },
  },
  gridItemDiagnoses: {
    width: '48%',
    flexBasis: '48%',
    flexShrink: 1,
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  gridItemCharge: {
    maxWidth: '11%',
    flexBasis: '11%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '33%',
      flexBasis: '33%',
    },
  },
  gridItemTotalCharge: {
    maxWidth: '8%',
    flexBasis: '8%',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '10%',
      flexBasis: '10%',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '25%',
      flexBasis: '25%',
    },
  },
  checkboxField: {
    marginRight: -14,
    marginTop: 0,
  },
  header: {
    padding: '0 8px 8px 16px',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    [theme.breakpoints.down('sm')]: {
      padding: '0 8px 4px 8px',
    },
  },
  body: {
    padding: '4px 16px 8px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '4px 8px 8px 8px',
    },
  },
  iconCalendarStart: {
    width: 14,
    height: 16,
    marginTop: -2,
  },
  iconCalendarEnd: {
    width: 14,
    height: 16,
    marginTop: -2,
  },
  iconCross: {
    width: 12,
    height: 12,
    color: theme.palette.grey[600],
  },
  rootGroupedField: {
    position: 'relative',
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: -40,
      right: -13,
      bottom: -40,
      left: -13,
      borderLeft: `2px solid ${theme.palette.primary[500]}`,
      borderRight: `2px solid ${theme.palette.primary[500]}`,
      pointerEvents: 'none',
      [theme.breakpoints.down('sm')]: {
        top: -20,
        right: -5,
        bottom: -20,
        left: -5,
      },
    },
  },
  rootGroupedFieldFirst: {
    '&:after': {
      top: -13,
      border: `2px solid ${theme.palette.primary[500]}`,
      borderBottom: 'none',
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
      [theme.breakpoints.down('sm')]: {
        top: -5,
      },
    },
  },
  rootGroupedFieldLast: {
    '&:after': {
      bottom: -13,
      border: `2px solid ${theme.palette.primary[500]}`,
      borderTop: 'none',
      borderBottomLeftRadius: 10,
      borderBottomRightRadius: 10,
      [theme.breakpoints.down('sm')]: {
        bottom: -5,
      },
    },
    '& + $rootGroupedFieldFirst': {
      marginTop: 36,
    },
  },
  rootOrField: {
    marginTop: 12,
  },
  rootError: {
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: 10,
      border: `1px solid ${theme.palette.validation[500]}`,
      pointerEvents: 'none',
    },
  },
  rootRejected: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.states.rejected)}, 0.12)`,
  },
  typographySeparator: {
    marginTop: 12,
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: '50%',
      left: 16,
      right: 'calc(50% + 24px)',
      height: 1,
      backgroundColor: theme.palette.primary.A100,
      pointerEvents: 'none',
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: '50%',
      right: 16,
      left: 'calc(50% + 24px)',
      height: 1,
      backgroundColor: theme.palette.primary.A100,
      pointerEvents: 'none',
    },
  },
  linkHCPC: {
    paddingTop: 8,
    fontWeight: 500,
  },
  tooltipPopper: {
    zIndex: 1290,
  },
  buttonDelete: {
    position: 'absolute',
    zIndex: 1,
    top: -10,
    width: 30,
    height: 30,
    minHeight: 30,
    right: -10,
    [theme.breakpoints.down('xs')]: {
      right: -7,
    },
  },
  deliveryFields: {
    marginTop: 6,
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
  gridContainer: {
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  gridItemCMNAMB: {
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      top: 15,
      right: 80,
    },
  },
  gridItemNarrative: {
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      top: 15,
      right: 40,
    },
  },
  gridItemDelivery: {
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      top: 15,
      right: 1,
    },
  },
});

class ProductClaimField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      descriptionTooltipOpen: false,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onRemoveButtonClick = this.onRemoveButtonClick.bind(this);
    this.onRemoveCancel = this.onRemoveCancel.bind(this);
    this.onRemoveSubmit = this.onRemoveSubmit.bind(this);
    this.onHCPCMouseEnter = this.onHCPCMouseEnter.bind(this);
    this.onHCPCMouseLeave = this.onHCPCMouseLeave.bind(this);
    this.onHCPCFocus = this.onHCPCFocus.bind(this);
    this.onHCPCBlur = this.onHCPCBlur.bind(this);
    this.onTooltipMouseLeave = this.onTooltipMouseLeave.bind(this);
    this.onTooltipMouseEnter = this.onTooltipMouseEnter.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { groupedField, groupedFieldFirst, groupedFieldLast, orField, onFieldBlur, onFieldFocus } = this.props;
    const { anchorEl, descriptionTooltipOpen } = this.state;
    if (nextProps.groupedField !== groupedFieldLast
      || nextProps.groupedFieldFirst !== groupedFieldFirst
      || nextProps.groupedFieldLast !== groupedFieldLast
      || nextProps.orField !== orField
      || nextState.anchorEl !== anchorEl
      || nextProps.onFieldBlur !== onFieldBlur
      || nextProps.onFieldFocus !== onFieldFocus
      || nextState.descriptionTooltipOpen !== descriptionTooltipOpen) {
      return true;
    }
    return false;
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onRemoveButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  onRemoveCancel() {
    this.setState({ anchorEl: null });
  }

  onRemoveSubmit() {
    this.setState({ anchorEl: null });
    this.props.onRemove();
  }

  onHCPCMouseEnter() {
    this.setState({ descriptionTooltipOpen: true });
  }

  onHCPCMouseLeave() {
    this.setState({ descriptionTooltipOpen: false });
  }

  onTooltipMouseLeave() {
    this.setState({ descriptionTooltipOpen: false });
  }

  onTooltipMouseEnter() {
    this.setState({ descriptionTooltipOpen: true });
  }

  onHCPCFocus() {
    this.setState({ descriptionTooltipOpen: true });
  }

  onHCPCBlur() {
    this.setState({ descriptionTooltipOpen: false });
  }

  render() {
    const {
      name,
      groupedField,
      groupedFieldFirst,
      groupedFieldLast,
      orField,
      cmnAmbDisabled,
      first,
      error,
      systemSettings,
      onFieldFocus,
      onFieldBlur,
      Diagnosis,
      Patient,
      formValues,
      onPrintEditPreviewButtonClick,
      rejectedField,
      disabled,
      classes,
    } = this.props;
    const { anchorEl, descriptionTooltipOpen } = this.state;
    const open = Boolean(anchorEl);
    return (
      <Fragment>
        {orField &&
          <Typography
            className={classes.typographySeparator}
            variant="subheading"
            color="secondary"
            align="center"
          >
            or
          </Typography>
        }
        <Paper
          className={classNames(classes.root, {
            [classes.rootFirst]: first,
            [classes.rootGroupedField]: groupedField,
            [classes.rootGroupedFieldFirst]: groupedFieldFirst,
            [classes.rootGroupedFieldLast]: groupedFieldLast,
            [classes.rootOrField]: orField,
            [classes.rootError]: error,
            [classes.rootRejected]: rejectedField,
          })}
          component="article"
        >
          <header className={classes.header}>
            <Button
              variant="fab"
              className={classes.buttonDelete}
              aria-label="Remove"
              aria-owns={open ? `${name}-product-claim-field-remove-menu` : null}
              aria-haspopup="true"
              onClick={this.onRemoveButtonClick}
            >
              <IconCross className={classes.iconCross} />
            </Button>
            <Menu
              id={`${name}-product-claim-field-remove-menu`}
              anchorEl={anchorEl}
              open={open}
              onClose={this.onMenuClose}
              list={false}
            >
              <DeleteMenu
                text="Are you sure you want to remove this item?"
                submitButtonText="Remove"
                onCancel={this.onRemoveCancel}
                onSubmit={this.onRemoveSubmit}
              />
            </Menu>
            <Grid
              className={classes.gridContainer}
              container
              justify="flex-start"
              alignItems="center"
              wrap="nowrap"
              spacing={8}
            >
                  <Grid item>
                    <Field
                      className={classes.checkboxField}
                      id={`${name}.Selected`}
                      name={`${name}.Selected`}
                      component={CheckboxField}
                    />
                  </Grid>
                  <Grid item> 
                    <Tooltip
                      popperClassName={classes.tooltipPopper}
                      content={
                        <Field
                          name={`${name}.Description`}
                          component={EditTextField}
                          edit
                          disabled={disabled}
                        />
                      }
                      placement="top"
                      disableHoverListener
                      open={descriptionTooltipOpen}
                      disablePointerEvents={false}
                      disableGutters
                      onMouseLeave={this.onTooltipMouseLeave}
                      onMouseEnter={this.onTooltipMouseEnter}
                    >
                      <Link
                        className={classes.linkHCPC}
                        onMouseOver={this.onHCPCMouseEnter}
                        onMouseLeave={this.onHCPCMouseLeave}
                        onFocus={this.onHCPCFocus}
                        onBlur={this.onHCPCBlur}
                      >
                        <Field
                          name={`${name}['HCPC S Code']`}
                          component={({ input }) => input.value}
                        />
                      </Link>
                    </Tooltip>
                    <Field
                      id={`${name}.Is Rejected`}
                      name={`${name}.Is Rejected`}
                      component={ProductClaimIsRejectedField}
                    />
                  </Grid>
                  <Grid className={classes.gridType} item>
                    <Fields
                      names={[
                        `${name}.Type`,
                        `${name}.ID`,
                      ]}
                      name={name}
                      component={ProductClaimTypeFields}
                      onFieldFocus={onFieldFocus}
                      onFieldBlur={onFieldBlur}
                      disabled={disabled}
                    />
                  </Grid>
                  <Fields
                    names={[
                      `${name}.Type`,
                      `${name}.Refill`,
                    ]}
                    name={name}
                    component={ProductClaimRefillFields}
                    onFieldFocus={onFieldFocus}
                    onFieldBlur={onFieldBlur}
                    disabled={disabled}
                  />
                  <Grid item xs={6} sm={3}>
                    <Fields
                      names={[
                        `${name}.assignedUserName`,
                        `${name}.assignedUserID`,
                        `${name}.assignedFromUserFirstName`,
                        `${name}.assignedFromUserLastName`,
                      ]}
                      name={name}
                      component={ProductClaimAssignedUserFields}
                      onFieldFocus={onFieldFocus}
                      onFieldBlur={onFieldBlur}
                      disabled={disabled}
                    />
                  </Grid>
                  <Fields
                    names={[
                      `${name}.Status Name`,
                      `${name}.Status`,
                      `${name}.Type`,
                    ]}
                    name={name}
                    component={ProductClaimStatusFields}
                    onFieldFocus={onFieldFocus}
                    onFieldBlur={onFieldBlur}
                    formValues={formValues}
                    disabled={disabled}
                  />
              <Grid item>
                <Grid
                  container
                  justify="space-between"
                  alignItems="center"
                  wrap="nowrap"
                  spacing={0}
                >
                  <Grid className={classes.gridItemCMNAMB} item>
                    <Field
                      name={`${name}['CMN/AMB']`}
                      component={ProductClaimCMNAMBField}
                      disabled={cmnAmbDisabled}
                      {...disabled ? { disabled: 'archive' } : {}}
                      onPrintEditPreviewButtonClick={onPrintEditPreviewButtonClick}
                    />
                  </Grid>
                  <Grid className={classes.gridItemNarrative} item>
                    <Field
                      name={`${name}.Narrative`}
                      component={ProductClaimNarrativeField}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid className={classes.gridItemDelivery} item>
                    <Fields
                      className={classes.deliveryFields}
                      names={[
                        `${name}.driverName`,
                        `${name}.selectedDriverName`,
                        `${name}.driverID`,
                        `${name}.placeOfSRV`,
                        `${name}.dateOfDelivery`,
                        `${name}.timeOfDelivery`,
                      ]}
                      nameKeys={{
                        driverID: 'driverID',
                        driverName: 'driverName',
                        selectedDriverName: 'selectedDriverName',
                        placeOfSRV: 'placeOfSRV',
                        dateOfDelivery: 'dateOfDelivery',
                        timeOfDelivery: 'timeOfDelivery',
                      }}
                      name={name}
                      component={ProductClaimDeliveryFields}
                      formValues={formValues}
                      onFieldFocus={onFieldFocus}
                      onFieldBlur={onFieldBlur}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </header>
          <div className={classes.body}>
            <Grid
              className={classes.gridContainer}
              container
              justify="flex-start"
              wrap="nowrap"
              spacing={8}
            >    
              <Grid className={classes.gridItemDateOfService} item>
                <Fields
                  id="Date of Service From / To"
                  names={[
                    `${name}.dateOfServiceFrom`,
                    `${name}.dateOfServiceTo`,
                  ]}
                  label="Date of Service (from/to)"
                  component={DatepickerField}
                  validate={[validate.required, validate.date]}
                  normalize={{
                    dateFrom: normalize.date,
                    dateTo: normalize.date,
                  }}
                  fullWidth
                  labelOverlap
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  variant="range"
                  startDateName={`${name}.dateOfServiceFrom`}
                  endDateName={`${name}.dateOfServiceTo`}
                  onInputFocus={event => onFieldFocus({
                    ...event.target.name === `${name}.dateOfServiceFrom` ? {
                      dateOfServiceFrom: event.target.value,
                    } : { dateOfServiceTo: event.target.value },
                  })}
                  onStartInputBlur={event => onFieldBlur({
                    dateOfServiceFrom: event.target.value,
                  })}
                  onEndInputBlur={event => onFieldBlur({
                    dateOfServiceTo: event.target.value,
                  })}
                  onStartDateSelect={(event, { dateFrom, dateTo }) => onFieldBlur({
                    dateOfServiceFrom: dateFrom,
                    dateOfServiceTo: dateTo,
                  })}
                  onEndDateSelect={(event, { dateFrom, dateTo }) => onFieldBlur({
                    dateOfServiceFrom: dateFrom,
                    dateOfServiceTo: dateTo,
                  })}
                  alwaysPrefillEndDate
                  inputAdornmentLgOnly
                  rangeButtons
                  disabled={disabled}
                />
              </Grid>
              <Grid className={classes.gridItemModifiers} item>
                <FieldArray
                  name={`${name}.Modifiers`}
                  label="Modifiers"
                  component={ProductClaimModifiersFieldArray}
                  onFieldFocus={onFieldFocus}
                  onFieldBlur={onFieldBlur}
                  disabled={disabled}
                />
              </Grid>
              <Grid className={classes.gridItemCharge} item>
                <Field
                  id={`${name}.Charge`}
                  name={`${name}.Charge`}
                  label="Charge"
                  component={TextField}
                  labelOverlap
                  validate={[validate.required]}
                  normalize={normalize.price}
                  fullWidth
                  disabled={disabled}
                />
              </Grid>   
              <Fields
                names={[
                  `${name}.Units`,
                  `${name}.Split for Claim`,
                  `${name}.Calories Per Unit`,
                ]}
                name={name}
                component={ProductClaimUnitsSplitForClaimFields}
                systemSettings={systemSettings}
                onFieldFocus={onFieldFocus}
                onFieldBlur={onFieldBlur}
                disabled={disabled}
              />
              <Grid className={classes.gridItemTotalCharge} item>
                <Fields
                  names={[
                    `${name}.Units`,
                    `${name}.Charge`,
                    `${name}.Calories Per Unit`,
                  ]}
                  name={name}
                  component={ProductClaimTotalChargeFields}
                />
              </Grid>
              <Grid className={classes.gridItemDiagnoses} item>
                <Fields
                  names={[
                    `${name}.diagnosisName`,
                    `${name}.Diagnosis`,
                    'Diagnosis ID',
                    'Patient ID',
                  ]}
                  Diagnosis={Diagnosis}
                  Patient={Patient}
                  name={name}
                  component={ProductClaimDiagnosisFields}
                  onFieldFocus={onFieldFocus}
                  onFieldBlur={onFieldBlur}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </div>
          <Fields
            names={[
              `${name}.Type`,
              `${name}.Refill`,
              `${name}.dateOfServiceFrom`,
            ]}
            name={name}
            component={ProductClaimRefillDatesField}
          />
        </Paper>
      </Fragment>
    );
  }
}

ProductClaimField.propTypes = {
  name: PropTypes.string.isRequired,
  onRemove: PropTypes.func,
  groupedField: PropTypes.bool,
  groupedFieldFirst: PropTypes.bool,
  groupedFieldLast: PropTypes.bool,
  orField: PropTypes.bool,
  cmnAmbDisabled: PropTypes.string,
  error: PropTypes.bool,
  first: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object.isRequired,
  onFieldFocus: PropTypes.func,
  onFieldBlur: PropTypes.func,
  onPrintEditPreviewButtonClick: PropTypes.func,
  Diagnosis: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
  }).isRequired,
  formValues: PropTypes.shape({
    'Primary Insurance ID': PropTypes.number,
  }),
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootFirst: PropTypes.string.isRequired,
    rootGroupedField: PropTypes.string.isRequired,
    rootGroupedFieldFirst: PropTypes.string.isRequired,
    rootGroupedFieldLast: PropTypes.string.isRequired,
    rootOrField: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
    gridType: PropTypes.string.isRequired,
    gridItemModifiers: PropTypes.string.isRequired,
    gridItemDiagnoses: PropTypes.string.isRequired,
    gridShrink: PropTypes.string.isRequired,
    checkboxField: PropTypes.string.isRequired,
    iconCalendarStart: PropTypes.string.isRequired,
    iconCalendarEnd: PropTypes.string.isRequired,
    iconCross: PropTypes.string.isRequired,
    typographySeparator: PropTypes.string.isRequired,
  }).isRequired,
};

ProductClaimField.defaultProps = {
  onRemove: () => {},
  groupedField: false,
  groupedFieldFirst: false,
  groupedFieldLast: false,
  orField: false,
  error: false,
  cmnAmbDisabled: null,
  first: false,
  onFieldFocus: () => {},
  onFieldBlur: () => {},
  onPrintEditPreviewButtonClick: () => {},
  disabled: false,
  formValues: {},
};

export default withStyles(styles)(ProductClaimField);



// WEBPACK FOOTER //
// ./src/components/workflow/ProductClaim/ProductClaimField.js